import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  SITE_ORIGIN,
  SITE_NAME,
  SITE_DEFAULT_DESCRIPTION,
  SITE_DEFAULT_OG_IMAGE,
  SITE_TWITTER_HANDLE,
  ORG_PHONE,
  ORG_ADDRESS,
  ORG_GEO,
  ORG_SAME_AS,
  findRouteSeo,
  RouteSeo,
} from "../../Constants/Seo";
import { ROUTE_VIDEOS, buildVideoObjectJsonLd } from "../../Constants/Videos";

type CmsMeta = { Label?: string; Value?: string };

type Props = {
  // Route path (e.g. "/about"). When supplied, the component pulls
  // title/description/canonical/breadcrumbs from the ROUTE_SEO table.
  path?: string;
  // Explicit overrides. Win against the ROUTE_SEO lookup.
  title?: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile";
  twitterCard?: "summary" | "summary_large_image";
  twitterImage?: string;
  // CMS-supplied meta (legacy shape from Strapi).
  meta?: CmsMeta[];
  // Extra JSON-LD documents (e.g. Person array on /about). Already typed as objects.
  jsonLd?: Record<string, any>[];
};

const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/eha_logo.png`,
  image: SITE_DEFAULT_OG_IMAGE,
  telephone: ORG_PHONE,
  priceRange: "$$$$",
  areaServed: [
    { "@type": "City", name: "Boca Raton" },
    { "@type": "State", name: "Florida" },
    { "@type": "Country", name: "United States" },
  ],
  serviceType: [
    "Financial Planning",
    "Wealth Management",
    "Retirement Planning",
    "Stock Option & Executive Compensation Planning",
    "Estate Planning",
    "Business Exit Planning",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: ORG_ADDRESS.street,
    addressLocality: ORG_ADDRESS.locality,
    addressRegion: ORG_ADDRESS.region,
    postalCode: ORG_ADDRESS.postalCode,
    addressCountry: ORG_ADDRESS.country,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: ORG_GEO.lat,
    longitude: ORG_GEO.lng,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: ORG_PHONE,
      contactType: "customer service",
      areaServed: "US",
      availableLanguage: ["English"],
    },
  ],
  sameAs: ORG_SAME_AS,
});

const buildBreadcrumbJsonLd = (route: RouteSeo) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: route.breadcrumb.map((b, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: b.name,
    item: `${SITE_ORIGIN}${b.path}`,
  })),
});

export const SEO = React.memo((props: Props) => {
  const {
    path,
    title: titleOverride,
    description: descOverride,
    canonical: canonicalOverride,
    ogTitle,
    ogDescription,
    ogImage,
    ogType,
    twitterCard,
    twitterImage,
    meta,
    jsonLd,
  } = props;

  const route = useMemo(() => (path ? findRouteSeo(path) : undefined), [path]);

  const title = titleOverride || route?.title || SITE_NAME;
  const description =
    descOverride || route?.description || SITE_DEFAULT_DESCRIPTION;
  const canonical =
    canonicalOverride || (path ? `${SITE_ORIGIN}${path}` : SITE_ORIGIN);
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgImage = ogImage || SITE_DEFAULT_OG_IMAGE;
  const finalOgType = ogType || route?.ogType || "website";
  const finalTwitterCard = twitterCard || "summary_large_image";
  const finalTwitterImage = twitterImage || finalOgImage;

  const cmsMeta = useMemo(
    () =>
      (meta || [])
        .filter((m) => m?.Label && m?.Value)
        .map((m) => ({ name: m.Label as string, content: m.Value as string })),
    [meta]
  );

  const jsonLdDocs = useMemo(() => {
    const docs: Record<string, any>[] = [buildOrganizationJsonLd()];
    if (route) docs.push(buildBreadcrumbJsonLd(route));
    if (path && ROUTE_VIDEOS[path]) {
      for (const id of ROUTE_VIDEOS[path]) {
        const v = buildVideoObjectJsonLd(id);
        if (v) docs.push(v);
      }
    }
    if (jsonLd?.length) docs.push(...jsonLd);
    return docs;
  }, [route, path, jsonLd]);

  return (
    <Helmet
      title={title}
      defaultTitle={SITE_NAME}
      htmlAttributes={{ lang: "en" }}
    >
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:type" content={finalOgType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content={finalTwitterCard} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      {SITE_TWITTER_HANDLE ? (
        <meta name="twitter:site" content={SITE_TWITTER_HANDLE} />
      ) : null}

      {cmsMeta.map((m) => (
        <meta key={`cms-${m.name}`} name={m.name} content={m.content} />
      ))}

      {jsonLdDocs.map((doc, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          // Helmet serializes children; stringify keeps the script body valid JSON.
        >
          {JSON.stringify(doc)}
        </script>
      ))}
    </Helmet>
  );
});
